import { Card, CardContent, Link, TableCell, Typography } from "@mui/material";
import type { Column } from "~/components/Table";
import {
  getActiveFiltersCount,
  makeRequestSchema,
  prepareListRequest,
  ResourceTable,
  useSearchRequest,
  withoutBaseTableModel,
  withSearchValidationErrorBoundary,
} from "~/components/Table";
import { auditColumns } from "~/domain/auditable";
import { useLogs } from "~/domain/datastores";
import { DataStoreLink, makeLogLocation, makeNewLogLocation } from "~/paths";
import type { Log } from "~/services/datastore";
import { listLogsSchema } from "../schemas";
import { LogFilters } from "./LogFilters";

const COLUMNS: ReadonlyArray<Column<Log>> = [
  {
    header: "Log",
    sortKey: "name",
    renderCell(log) {
      return (
        <TableCell>
          {log.name}
          <Link
            component={DataStoreLink}
            to={makeLogLocation({ logId: log.id })}
          >
            <Typography variant="body2">{log.id}</Typography>
          </Link>
        </TableCell>
      );
    },
  },
  {
    accessor: "startTime",
    dataType: "timestamp",
    isSortable: true,
  },
  {
    accessor: "endTime",
    dataType: "timestamp",
    isSortable: true,
  },
  {
    accessor: "recordSize",
    dataType: "bytes",
    isSortable: true,
  },
  {
    accessor: "recordCount",
    dataType: "number",
    isSortable: true,
  },
  {
    accessor: "objectSize",
    dataType: "bytes",
    isSortable: true,
  },
  {
    accessor: "objectCount",
    dataType: "number",
    isSortable: true,
  },
  {
    accessor: "defaultWorkflowId",
    dataType: "foreign-key",
    resourceType: "workflow",
    defaultHidden: true,
  },
  {
    accessor: "locked",
    dataType: "boolean",
    isSortable: true,
    defaultHidden: true,
  },
  {
    accessor: "lockedBy",
    dataType: "foreign-key",
    resourceType: "user",
    defaultHidden: true,
  },
  {
    accessor: "lockedAt",
    dataType: "datetime",
    isSortable: true,
    defaultHidden: true,
  },
  {
    accessor: "groupId",
    dataType: "foreign-key",
    resourceType: "group",
  },
  ...auditColumns,
];

const requestSchema = makeRequestSchema(COLUMNS, listLogsSchema);

export const LogTable = withSearchValidationErrorBoundary(function LogTable() {
  const [request, setRequest] = useSearchRequest(requestSchema);

  const searchQuery = useLogs(prepareListRequest(request), {
    keepPreviousData: true,
    cacheTime: 0,
  });

  const filterValues = withoutBaseTableModel(request);

  return (
    <Card>
      <CardContent>
        <ResourceTable
          resourceName="log"
          resourceCreateLocation={makeNewLogLocation()}
          getRowKey={(log) => log.id}
          columns={COLUMNS}
          searchQuery={searchQuery}
          tableModel={request}
          onTableModelChange={setRequest}
          filterSection={
            <LogFilters values={filterValues} setValues={setRequest} />
          }
          activeFilterCount={getActiveFiltersCount(filterValues)}
        />
      </CardContent>
    </Card>
  );
});
